/**
 * 分享页要两边的数据，和个人页同一个道理：
 *
 *   account   邀请码、链接、各级人数、下级名单、规则   —— 纯读
 *   gateway   领返点、开宝箱                          —— 要写钱包，只能它来
 *
 * **钱包只有一个写者**（ADR 0001）。返点在下级充值时只记了一行账，
 * 上级点"领取"才真的进钱包 —— 当场发的话就是写另一个玩家的钱包，
 * 而那个玩家可能绑在另一台实例上。
 */

/** 一档宝箱。`canClaim` / `left` 都是**服务端算的**，前端不自己减。 */
export interface ChestState {
  need: number;
  cash: string;
  freeSpins: number;
  unlocked: boolean;
  claimed: boolean;
  canClaim: boolean;
  /** 还差几个有效一级下级 */
  left: number;
}

export interface LevelRule {
  level: number;
  rate: string;
  ratePercent: number;
  freeSpins: number;
}

/** 规则说明。**前端不维护一份** —— 后台改了比例，抄的那份就悄悄过期了。 */
export interface ReferralRules {
  configVersion: string;
  levels: number;
  effective: { depositMin: string; betMin: string };
  rules: LevelRule[];
  chests: { need: number; cash: string; freeSpins: number }[];
}

export interface ReferralCounts {
  d1: number; d2: number; d3: number;
  e1: number; e2: number; e3: number;
}

export interface ReferralSummary {
  code: string;
  /** 没配分享前缀时是空串 —— 那时只显示邀请码，不显示一条半截的链接 */
  link: string;
  counts: ReferralCounts;
  pending: { count: number; amount: number; freeSpins: number };
  chests: ChestState[];
  rules: ReferralRules;
}

export interface DownlineItem {
  /** **打过码的账号**（前六后四）。全量 uid 不发给另一个玩家。 */
  id: string;
  nickname: string | null;
  effective: boolean;
  ts: number | null;
}

export interface DownlinePage {
  level: number;
  total: number;
  offset: number;
  limit: number;
  items: DownlineItem[];
}

export interface ClaimResult {
  amount: number;
  freeSpins: number;
  count: number;
  bal: number;
  replayed: boolean;
}

export interface ChestResult {
  need: number;
  reward: { cash: string; freeSpins: number };
  bal: number;
  freeSpins: number;
  effectiveL1: number;
}

//: 从链接里捡到的邀请码存在这儿。**注册成功之前一直留着** ——
//: 玩家点开链接、逛半天、明天才注册，那也该算这个上级的。
const REF_KEY = 'lobby.ref';

/**
 * 从 URL 上把 `?ref=` 捡走并记下来，然后**把它从地址栏里抹掉**。
 *
 * 抹掉是有意的：留在地址栏里的话，玩家把自己的地址栏复制给下一个人，
 * 推荐关系就串到上一个人头上去了。
 */
export function captureRef(): void {
  try {
    const url = new URL(location.href);
    const ref = url.searchParams.get('ref');
    if (!ref) return;
    localStorage.setItem(REF_KEY, ref.trim().slice(0, 32));
    url.searchParams.delete('ref');
    history.replaceState(null, '', url.toString());
  } catch { /* 隐私模式下 localStorage 会抛。捡不到就当自然注册 */ }
}

export function pendingRef(): string | undefined {
  try {
    return localStorage.getItem(REF_KEY) || undefined;
  } catch { return undefined; }
}

export function clearRef(): void {
  try { localStorage.removeItem(REF_KEY); } catch { /* 同上 */ }
}
